if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.viewport = this.querySelector('[data-product-gallery-viewport]');
      this.items = [...this.querySelectorAll('[data-media-index]:not([data-gallery-clone])')];
      this.loopBeforeItems = [...this.querySelectorAll('[data-gallery-clone="before"]')];
      this.loopAfterItems = [...this.querySelectorAll('[data-gallery-clone="after"]')];
      this.controls = this.querySelector('.product-gallery__controls');
      this.counter = this.querySelector('[data-product-gallery-counter]');
      this.nextButton = this.querySelector('[data-product-gallery-next]');
      this.cursor = this.querySelector('[data-product-gallery-cursor]');
      this.activeIndex = Math.max(0, this.items.findIndex(item => String(item.dataset.mediaId) === String(this.dataset.initialMediaId)));
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.desktopLayout = matchMedia('(min-width: 990px)');
      this.scrollFrame = null;
      this.loopEnabled = false;
      this.loopStart = 0;
      this.loopCycleWidth = 0;
      this.isAnimating = false;
      this.jumpFrame = null;

      this.items.forEach(item => {
        if (!item.querySelector('video, iframe, model-viewer, a, button')) item.classList.add('is-gallery-cursor-target');
      });

      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1), { signal: this.abortController.signal });
      this.viewport?.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.abortController.signal });
      this.viewport?.addEventListener('pointerdown', event => this.onPointerDown(event), { passive: true, signal: this.abortController.signal });
      this.viewport?.addEventListener('pointermove', event => this.updateCursor(event), { passive: true, signal: this.abortController.signal });
      this.viewport?.addEventListener('pointerup', event => this.onPointerUp(event), { passive: true, signal: this.abortController.signal });
      this.viewport?.addEventListener('pointerleave', () => this.hideCursor(), { passive: true, signal: this.abortController.signal });
      this.finePointer.addEventListener('change', () => this.updateInteractivity(), { signal: this.abortController.signal });
      this.desktopLayout.addEventListener('change', () => this.refreshLayout(), { signal: this.abortController.signal });
      window.addEventListener('resize', () => this.refreshLayout(), { passive: true, signal: this.abortController.signal });

      requestAnimationFrame(() => {
        this.configureLoop();
        this.updateInteractivity();
        this.updateCounter();
        if (this.isHorizontal()) this.goTo(this.activeIndex, 'auto');
      });
    }

    isHorizontal() {
      if (!this.viewport) return false;
      return this.viewport.scrollWidth > this.viewport.clientWidth + 1;
    }

    updateInteractivity() {
      const horizontal = this.isHorizontal();
      if (this.controls) this.controls.hidden = !horizontal;
      this.classList.toggle('is-cursor-ready', Boolean(this.cursor && horizontal && this.dataset.galleryLayout === 'immersive' && this.finePointer.matches));
      if (!this.classList.contains('is-cursor-ready')) this.hideCursor();
    }

    refreshLayout() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.configureLoop();
        this.updateInteractivity();
        this.updateActiveIndex();
      });
    }

    configureLoop() {
      this.loopEnabled = Boolean(
        this.dataset.galleryLayout === 'immersive'
        && this.desktopLayout.matches
        && this.items.length > 1
        && this.loopBeforeItems.length === this.items.length
        && this.loopAfterItems.length === this.items.length
      );
      this.loopStart = 0;
      this.loopCycleWidth = 0;
      if (!this.viewport || !this.items.length) return;
      if (this.loopEnabled) {
        this.loopStart = this.items[0].offsetLeft;
        this.loopCycleWidth = this.loopAfterItems[0].offsetLeft - this.loopStart;
        if (this.loopCycleWidth <= 0) this.loopEnabled = false;
      }
      this.jumpTo(this.items[this.activeIndex]?.offsetLeft || 0);
    }

    jumpTo(targetLeft) {
      if (!this.viewport) return;
      if (this.jumpFrame) cancelAnimationFrame(this.jumpFrame);
      this.viewport.classList.add('is-gallery-jumping');
      this.viewport.getBoundingClientRect();
      this.viewport.scrollLeft = targetLeft;
      this.jumpFrame = requestAnimationFrame(() => {
        this.viewport?.classList.remove('is-gallery-jumping');
        this.jumpFrame = null;
      });
    }

    updateCursor(event) {
      if (!this.classList.contains('is-cursor-ready')) return;
      const target = event.target.closest('.is-gallery-cursor-target');
      this.classList.toggle('is-cursor-visible', Boolean(target));
      if (!target) return;
      const bounds = this.getBoundingClientRect();
      this.cursor.style.transform = `translate3d(${event.clientX - bounds.left}px, ${event.clientY - bounds.top}px, 0) translate(-50%, -50%)`;
    }

    onPointerDown(event) {
      if (!this.classList.contains('is-cursor-ready') || event.button !== 0) return;
      if (!event.target.closest('.is-gallery-cursor-target')) return;
      cancelAnimationFrame(this.scrollAnimationFrame);
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      if (!this.pointerStart || !event.target.closest('.is-gallery-cursor-target')) {
        this.pointerStart = null;
        return;
      }
      const distance = Math.hypot(event.clientX - this.pointerStart.x, event.clientY - this.pointerStart.y);
      this.pointerStart = null;
      if (distance <= 6) this.goTo(this.activeIndex + 1);
    }

    hideCursor() {
      this.classList.remove('is-cursor-visible');
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        if (!this.isAnimating) this.normalizeLoopPosition();
        this.updateActiveIndex();
      });
    }

    normalizeLoopPosition() {
      if (!this.loopEnabled || !this.viewport || this.loopCycleWidth <= 0) return false;
      const scrollLeft = this.viewport.scrollLeft;
      let normalizedLeft = scrollLeft;
      if (scrollLeft < this.loopStart - 1) normalizedLeft = scrollLeft + this.loopCycleWidth;
      else if (scrollLeft >= this.loopStart + this.loopCycleWidth - 1) normalizedLeft = scrollLeft - this.loopCycleWidth;
      if (Math.abs(normalizedLeft - scrollLeft) < 1) return false;
      this.jumpTo(normalizedLeft);
      return true;
    }

    updateActiveIndex() {
      if (!this.viewport || !this.items.length || !this.isHorizontal()) return;
      const scrollLeft = this.viewport.scrollLeft;
      const renderedItems = this.loopEnabled
        ? [...this.loopBeforeItems, ...this.items, ...this.loopAfterItems]
        : this.items;
      let closestIndex = 0;
      let closestDistance = Infinity;
      renderedItems.forEach(item => {
        const distance = Math.abs(item.offsetLeft - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = Number(item.dataset.mediaIndex) || 0;
        }
      });
      if (closestIndex === this.activeIndex) return;
      this.activeIndex = closestIndex;
      this.updateCounter();
      this.pauseInactiveMedia();
    }

    updateCounter() {
      if (this.counter) this.counter.textContent = `${this.activeIndex + 1} / ${this.items.length}`;
    }

    pauseInactiveMedia() {
      this.items.forEach((item, index) => {
        if (index !== this.activeIndex) item.querySelectorAll('video').forEach(video => video.pause());
      });
    }

    goTo(index, behavior = this.reducedMotion ? 'auto' : 'smooth') {
      if (!this.items.length) return;
      const targetIndex = (index + this.items.length) % this.items.length;
      let target = this.items[targetIndex];
      if (this.loopEnabled && index >= this.items.length) target = this.loopAfterItems[targetIndex];
      else if (this.loopEnabled && index < 0) target = this.loopBeforeItems[targetIndex];
      this.activeIndex = targetIndex;
      this.updateCounter();
      this.pauseInactiveMedia();
      if (this.isHorizontal()) {
        if (behavior === 'smooth') this.animateTo(target.offsetLeft);
        else {
          this.jumpTo(target.offsetLeft);
          this.normalizeLoopPosition();
        }
      }
      else target.scrollIntoView({ behavior, block: 'center', inline: 'nearest' });
    }

    animateTo(targetLeft) {
      cancelAnimationFrame(this.scrollAnimationFrame);
      this.isAnimating = true;
      const startLeft = this.viewport.scrollLeft;
      const distance = targetLeft - startLeft;
      if (Math.abs(distance) < 1) {
        this.isAnimating = false;
        this.normalizeLoopPosition();
        return;
      }
      const startTime = performance.now();
      const duration = Math.min(460, Math.max(280, Math.abs(distance) * .55));
      const step = currentTime => {
        const progress = Math.min(1, (currentTime - startTime) / duration);
        const eased = 1 - Math.pow(1 - progress, 4);
        this.viewport.scrollLeft = startLeft + distance * eased;
        if (progress < 1) this.scrollAnimationFrame = requestAnimationFrame(step);
        else {
          this.isAnimating = false;
          this.normalizeLoopPosition();
          this.updateActiveIndex();
        }
      };
      this.scrollAnimationFrame = requestAnimationFrame(step);
    }

    selectMedia(mediaId) {
      const index = this.items.findIndex(item => String(item.dataset.mediaId) === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      if (this.jumpFrame) cancelAnimationFrame(this.jumpFrame);
      if (this.scrollAnimationFrame) cancelAnimationFrame(this.scrollAnimationFrame);
      this.viewport?.classList.remove('is-gallery-jumping');
    }
  });
}

if (!customElements.get('product-information')) {
  customElements.define('product-information', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        this.states = JSON.parse(this.querySelector('[data-product-states]')?.textContent || '[]');
      } catch {
        this.states = [];
      }
      this.addEventListener('variant:change', event => {
        const variant = event.detail?.variant;
        const handleOption = this.querySelector('.variant-picker__option[data-handle-option]');
        const removable = this.querySelector('[data-handle-removable-addon]:checked');
        if (handleOption && removable && variant?.options?.[Number(handleOption.dataset.optionPosition) - 1] !== 'Sans Manche') {
          removable.checked = false;
          this.updatePurchaseSummaries();
        }
        this.updateVariant(variant);
        this.refreshHandleChoices();
      }, { signal: this.abortController.signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-selling-plan-select]')) this.syncSellingPlanUrl(event.target.value);
        if (event.target.matches('[data-product-addon-option]:checked')) {
          if (event.target.matches('[data-handle-removable-addon]')) {
            const withoutHandle = [...this.querySelectorAll('.variant-picker__option[data-handle-option] .variant-option__input')]
              .find(input => input.value === 'Sans Manche' && !input.disabled);
            if (withoutHandle && !withoutHandle.checked) {
              withoutHandle.checked = true;
              withoutHandle.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          const group = event.target.dataset.addonExclusiveGroup;
          if (group) {
            this.querySelectorAll('[data-product-addon-option][data-addon-exclusive-group]').forEach(input => {
              if (input !== event.target && input.dataset.addonExclusiveGroup === group && input.checked) input.checked = false;
            });
          }
        }
        if (event.target.matches('[data-product-addon-option], .quantity-selector__input')) this.updatePurchaseSummaries();
        this.refreshHandleChoices();
      }, { signal: this.abortController.signal });
      this.addEventListener('click', event => {
        const clearButton = event.target.closest('[data-addon-clear]');
        if (clearButton) {
          const group = clearButton.dataset.addonClearGroup;
          const selector = group ? `[data-product-addon-option][data-addon-exclusive-group="${CSS.escape(group)}"]:checked` : '[data-product-addon-option]:checked';
          this.querySelectorAll(selector).forEach(input => {
            input.checked = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          return;
        }
        const navigationButton = event.target.closest('[data-cross-sell-previous], [data-cross-sell-next]');
        if (!navigationButton) return;
        const viewport = navigationButton.closest('.product-cross-sell')?.querySelector('[data-cross-sell-viewport]');
        if (!viewport) return;
        const direction = navigationButton.matches('[data-cross-sell-previous]') ? -1 : 1;
        viewport.scrollBy({ left: direction * Math.max(viewport.clientWidth * .82, 220), behavior: this.reducedMotion ? 'auto' : 'smooth' });
      }, { signal: this.abortController.signal });
      this.setupHandleChoices();
      this.updatePurchaseSummaries(this.currentProductState());
      this.setupStickyAddToCart();
    }

    setupHandleChoices() {
      this.handleChoices?.removableButton?.remove();
      this.handleChoices?.withoutButton?.remove();
      this.handleChoices?.without?.closest('.variant-swatch')?.classList.remove('variant-swatch--handle-proxy');
      this.handleChoices = null;
      this.classList.remove('product-information--handle-choices-ready');
      const picker = this.querySelector('variant-picker[data-unified-handle-selector="true"]');
      const option = picker?.querySelector('.variant-picker__option[data-handle-option]');
      const swatches = option?.querySelector('.variant-swatches');
      const removable = this.querySelector('[data-handle-removable-addon]');
      if (!swatches || !removable) return;

      const radios = [...swatches.querySelectorAll('.variant-option__input')];
      const fixed = radios.find(input => input.value === 'Fixe');
      const without = radios.find(input => input.value === 'Sans Manche');
      if (!fixed || !without) return;

      const removableCard = removable.closest('.product-addon');
      const title = removableCard?.querySelector('.product-addon__title')?.textContent?.trim();
      if (!title) return;
      const makeChoice = (className, label, description, price) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.setAttribute('aria-pressed', 'false');
        const name = document.createElement('span');
        name.className = 'product-handle-choice__name';
        name.textContent = label;
        button.append(name);
        if (price) {
          const badge = document.createElement('span');
          badge.className = 'product-handle-choice__price';
          badge.textContent = price;
          button.append(badge);
        }
        if (description) {
          const detail = document.createElement('small');
          detail.className = 'product-handle-choice__detail';
          detail.textContent = description;
          button.append(detail);
        }
        return button;
      };
      const removableButton = makeChoice(
        'product-handle-choice product-handle-choice--removable',
        title,
        removableCard.querySelector('.product-addon__text')?.textContent?.trim(),
        removableCard.querySelector('.product-addon__price')?.textContent?.trim()
      );
      removableButton.disabled = removable.disabled;
      const withoutButton = makeChoice('product-handle-choice product-handle-choice--none', picker.dataset.handleNoneLabel);
      const withoutSwatch = without.closest('.variant-swatch');
      withoutSwatch.before(removableButton);
      withoutSwatch.after(withoutButton);
      withoutSwatch.classList.add('variant-swatch--handle-proxy');
      this.handleChoices = { fixed, without, removable, removableButton, withoutButton };
      removableButton.addEventListener('click', () => {
        if (!removable.checked && !removable.disabled) {
          removable.checked = true;
          removable.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.refreshHandleChoices();
      }, { signal: this.abortController.signal });
      withoutButton.addEventListener('click', () => {
        if (removable.checked) {
          removable.checked = false;
          removable.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (!without.checked && !without.disabled) {
          without.checked = true;
          without.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.refreshHandleChoices();
      }, { signal: this.abortController.signal });
      this.classList.add('product-information--handle-choices-ready');
      this.refreshHandleChoices();
    }

    refreshHandleChoices() {
      if (!this.handleChoices) return;
      const { without, removable, removableButton, withoutButton } = this.handleChoices;
      const removableSelected = removable.checked;
      const withoutSelected = without.checked && !removableSelected;
      removableButton.classList.toggle('is-active', removableSelected);
      removableButton.setAttribute('aria-pressed', String(removableSelected));
      withoutButton.classList.toggle('is-active', withoutSelected);
      withoutButton.setAttribute('aria-pressed', String(withoutSelected));
      withoutButton.disabled = without.disabled;
    }

    setupStickyAddToCart() {
      this.stickyBar = this.querySelector('[data-sticky-add-to-cart]');
      if (!this.stickyBar) return;
      this.purchaseRow = this.querySelector('.product-form__purchase-row');
      this.mainSubmit = this.querySelector('[data-product-submit]');
      this.stickySubmit = this.querySelector('[data-sticky-product-submit]');
      this.stickyFooterBoundary = document.querySelector('theme-footer');
      const changeVariant = this.querySelector('[data-sticky-change-variant]');
      const signal = this.abortController.signal;

      this.queueStickyVisibility = () => {
        if (this.stickyVisibilityFrame) return;
        this.stickyVisibilityFrame = requestAnimationFrame(() => {
          this.stickyVisibilityFrame = null;
          if (!this.purchaseRow || !this.stickyBar) return;
          const headerOffset = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--site-header-offset')) || 0;
          const purchaseBounds = this.purchaseRow.getBoundingClientRect();
          const viewportHeight = document.documentElement.clientHeight;
          const footerTop = this.stickyFooterBoundary?.getBoundingClientRect().top ?? Infinity;
          const passedPurchaseRow = purchaseBounds.bottom < headerOffset + 8;
          const beforeFooter = footerTop > viewportHeight - this.stickyBar.offsetHeight;
          const shouldShow = passedPurchaseRow && beforeFooter;
          this.stickyBar.classList.toggle('is-visible', shouldShow);
          this.stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        });
      };

      this.stickySubmit?.addEventListener('click', () => {
        if (!this.mainSubmit?.disabled) this.mainSubmit?.click();
      }, { signal });
      changeVariant?.addEventListener('click', () => {
        const picker = this.querySelector('[data-product-variant-picker]');
        if (!picker) return;
        picker.querySelector('summary, input:not([type="hidden"])')?.focus({ preventScroll: true });
        picker.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' });
      }, { signal });
      window.addEventListener('scroll', this.queueStickyVisibility, { passive: true, signal });
      window.addEventListener('resize', this.queueStickyVisibility, { passive: true, signal });

      if (this.mainSubmit) {
        this.stickySubmitObserver = new MutationObserver(() => this.syncStickySubmitState());
        this.stickySubmitObserver.observe(this.mainSubmit, { attributes: true, attributeFilter: ['disabled', 'aria-busy'] });
      }
      this.syncStickySubmitState();
      this.queueStickyVisibility();
    }

    syncStickySubmitState() {
      if (!this.mainSubmit || !this.stickySubmit) return;
      this.stickySubmit.disabled = this.mainSubmit.disabled;
      if (this.mainSubmit.getAttribute('aria-busy') === 'true') this.stickySubmit.setAttribute('aria-busy', 'true');
      else this.stickySubmit.removeAttribute('aria-busy');
    }

    updateVariant(variant) {
      if (!variant) return;
      const state = this.states.find(item => String(item.id) === String(variant.id));
      if (!state) return;
      const animatedElements = [];

      const price = this.querySelector('[data-product-price]');
      if (price) {
        price.innerHTML = state.priceHtml;
        animatedElements.push(price);
      }

      const submit = this.querySelector('[data-product-submit]');
      if (submit) {
        submit.disabled = !state.available;
        const label = submit.querySelector('.button__label');
        const value = submit.querySelector('.button__price');
        if (label) {
          label.textContent = state.available ? this.dataset.addToCartLabel : this.dataset.soldOutLabel;
          animatedElements.push(label);
        }
        if (value) {
          value.textContent = state.price;
          animatedElements.push(value);
        }
      }

      const payment = this.querySelector('[data-product-payment]');
      if (payment) payment.hidden = !state.available;

      const stickySubmit = this.querySelector('[data-sticky-product-submit]');
      if (stickySubmit) {
        stickySubmit.disabled = !state.available;
        const stickyLabel = stickySubmit.querySelector('.button__label');
        const stickyPrice = stickySubmit.querySelector('.button__price');
        if (stickyLabel) {
          stickyLabel.textContent = state.available ? this.dataset.addToCartLabel : this.dataset.soldOutLabel;
          animatedElements.push(stickyLabel);
        }
        if (stickyPrice) {
          stickyPrice.textContent = state.price;
          animatedElements.push(stickyPrice);
        }
      }
      const stickyVariantTitle = this.querySelector('[data-sticky-variant-title]');
      if (stickyVariantTitle) animatedElements.push(stickyVariantTitle);
      const stickyMainImage = this.updateStickyMainImage(state);
      if (stickyMainImage) animatedElements.push(stickyMainImage);

      const formVariant = this.querySelector('[data-product-form-variant]');
      if (formVariant) formVariant.value = state.id;

      this.updateSellingPlans(state);

      const quantity = this.querySelector('.quantity-selector__input');
      if (quantity) {
        quantity.disabled = !state.available;
        quantity.min = state.quantityMin;
        quantity.step = state.quantityStep;
        if (state.quantityMax) quantity.max = state.quantityMax;
        else quantity.removeAttribute('max');
        if (Number(quantity.value) < Number(state.quantityMin)) quantity.value = state.quantityMin;
      }
      this.querySelectorAll('.quantity-selector__button').forEach(button => { button.disabled = !state.available; });

      this.updateInventory(state);
      animatedElements.push(...this.querySelectorAll('[data-product-inventory]'));
      this.updateInstallments(state);
      this.updatePurchaseSummaries(state);

      this.querySelector('product-gallery')?.showVariantMedia(state.featuredMediaId);
      this.animateVariantUpdate(animatedElements);

      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', state.id);
        const sellingPlan = this.querySelector('[data-selling-plan-select]')?.value;
        if (sellingPlan) url.searchParams.set('selling_plan', sellingPlan);
        else url.searchParams.delete('selling_plan');
        window.history.replaceState({}, '', url);
      }
      this.queueStickyVisibility?.();
    }

    currentProductState() {
      const variantId = this.querySelector('variant-picker input[type="hidden"][data-variant-id], [data-product-form-variant]')?.value;
      return this.states.find(item => String(item.id) === String(variantId)) || this.states[0];
    }

    updateInstallments(state) {
      this.querySelectorAll('[data-product-installments]').forEach(container => {
        container.hidden = !state?.available;
        const amount = container.querySelector('[data-installment-amount]');
        const data = container.querySelector('[data-installment-amounts]');
        if (!amount || !data || !state) return;
        try {
          const amounts = JSON.parse(data.textContent || '{}');
          if (amounts[String(state.id)]) amount.textContent = amounts[String(state.id)];
        } catch {}
      });
    }

    updatePurchaseSummaries(state = this.currentProductState()) {
      const selectedAddons = [...this.querySelectorAll('[data-product-addon-option]:checked:not(:disabled)')];
      const removableHandle = selectedAddons.find(addon => addon.matches('[data-handle-removable-addon]'));
      this.querySelectorAll('[data-product-purchase-summary]').forEach(summary => {
        summary.querySelectorAll('[data-summary-option-index]').forEach(row => {
          const optionIndex = Number(row.dataset.summaryOptionIndex);
          const value = row.querySelector('[data-summary-option-value]');
          if (!value || state?.options?.[optionIndex] === undefined) return;
          const optionValue = state.options[optionIndex];
          value.textContent = removableHandle && optionValue === 'Sans Manche'
            ? `${removableHandle.dataset.addonTitle || 'Amovible'} · ${removableHandle.dataset.addonPrice?.replace(/\s+/g, ' ').trim() || ''}`.trim()
            : optionValue;
        });

        const quantity = summary.querySelector('[data-summary-quantity]');
        const quantityInput = this.querySelector('.product-form .quantity-selector__input');
        if (quantity && quantityInput) quantity.textContent = quantityInput.value;

        const addonsRow = summary.querySelector('[data-summary-addons]');
        const addonsList = summary.querySelector('[data-summary-addon-list]');
        if (!addonsRow || !addonsList) return;
        const fragment = document.createDocumentFragment();
        selectedAddons.filter(addon => addon !== removableHandle).forEach(addon => {
          const item = document.createElement('li');
          item.className = 'product-purchase-summary__addon';
          const title = document.createElement('span');
          title.textContent = addon.dataset.addonTitle || '';
          item.append(title);
          if (addon.dataset.addonPrice) {
            const price = document.createElement('span');
            price.textContent = addon.dataset.addonPrice.replace(/\s+/g, ' ').trim().replace(/^\+\s*/, '');
            item.append(price);
          }
          fragment.append(item);
        });
        addonsList.replaceChildren(fragment);
        addonsRow.hidden = selectedAddons.length === (removableHandle ? 1 : 0);
      });
      this.updateStickySelectionSummary(state, selectedAddons);
      this.updateStickyAddonThumbnails(selectedAddons);
      this.updatePurchaseButtonTotals(state, selectedAddons);
    }

    updatePurchaseButtonTotals(state, selectedAddons = []) {
      if (!state) return;
      const quantityInput = this.querySelector('.product-form .quantity-selector__input');
      const quantity = Math.max(1, Number(quantityInput?.value) || 1);
      const addonTotal = selectedAddons.reduce((total, addon) => total + (Number(addon.dataset.addonPriceCents) || 0), 0);
      const total = ((Number(state.priceAmount) || 0) * quantity) + addonTotal;
      const formattedTotal = this.formatMoney(total);

      this.querySelectorAll('[data-product-submit], [data-sticky-product-submit]').forEach(button => {
        const price = button.querySelector('.button__price');
        if (price) price.textContent = formattedTotal;
      });
    }

    formatMoney(amount) {
      const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      const token = moneyFormat.match(/{{\s*(\w+)\s*}}/)?.[1] || 'amount';
      const hideZeros = this.dataset.hideTrailingZeros === 'true' && amount % 100 === 0;
      const commaDecimal = token.includes('comma_separator');
      const spaceThousands = token.includes('space_separator');
      const apostropheThousands = token.includes('apostrophe_separator');
      const noDecimals = hideZeros || token.includes('no_decimals');
      const decimalMark = commaDecimal ? ',' : '.';
      const thousandsMark = spaceThousands ? ' ' : apostropheThousands ? "'" : commaDecimal ? '.' : ',';
      const decimals = noDecimals ? 0 : 2;
      const numeric = (amount / 100).toFixed(decimals);
      const [integer, fraction] = numeric.split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsMark);
      const formattedAmount = decimals ? `${grouped}${decimalMark}${fraction}` : grouped;
      const formattedMoney = moneyFormat.replace(/{{\s*\w+\s*}}/, formattedAmount);
      const template = document.createElement('template');
      template.innerHTML = formattedMoney;
      return template.content.textContent || formattedMoney;
    }

    updateStickySelectionSummary(state, selectedAddons = []) {
      const summary = this.querySelector('[data-sticky-variant-title]');
      if (!summary) return;
      const parts = [];
      const removableHandle = selectedAddons.find(addon => addon.matches('[data-handle-removable-addon]'));
      if (summary.dataset.hasVariants === 'true' && state?.options) {
        parts.push(state.options.map(value => removableHandle && value === 'Sans Manche' ? removableHandle.dataset.addonTitle || 'Amovible' : value).join(' / '));
      }
      selectedAddons.forEach(addon => {
        if (addon === removableHandle) return;
        const title = addon.dataset.addonTitle?.trim();
        if (title) parts.push(title);
      });
      summary.textContent = parts.join(' · ');
      summary.hidden = parts.length === 0;
    }

    updateStickyMainImage(state) {
      const image = this.querySelector('.product-sticky-atc__thumbnail--main .product-sticky-atc__image');
      if (!(image instanceof HTMLImageElement) || !state?.featuredImageUrl) return null;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = state.featuredImageUrl;
      return image;
    }

    updateStickyAddonThumbnails(selectedAddons = []) {
      const container = this.querySelector('[data-sticky-addon-thumbnails]');
      if (!container) return;
      const fragment = document.createDocumentFragment();
      selectedAddons.forEach(addon => {
        const imageUrl = addon.dataset.addonImage;
        if (!imageUrl) return;
        const thumbnail = document.createElement('span');
        thumbnail.className = 'product-sticky-atc__thumbnail product-sticky-atc__thumbnail--addon';
        thumbnail.title = addon.dataset.addonTitle || '';
        const image = document.createElement('img');
        image.className = 'product-sticky-atc__image';
        image.src = imageUrl;
        image.alt = '';
        image.width = 36;
        image.height = 36;
        image.loading = 'lazy';
        thumbnail.append(image);
        fragment.append(thumbnail);
      });
      container.replaceChildren(fragment);
      container.hidden = container.childElementCount === 0;
    }

    updateSellingPlans(state) {
      const container = this.querySelector('[data-selling-plans]');
      const select = container?.querySelector('[data-selling-plan-select]');
      if (!container || !select) return;

      const previousValue = select.value;
      const plans = Array.isArray(state.sellingPlans) ? state.sellingPlans : [];
      const fragment = document.createDocumentFragment();

      if (!state.requiresSellingPlan) {
        const oneTimeOption = document.createElement('option');
        oneTimeOption.value = '';
        oneTimeOption.textContent = select.dataset.oneTimeLabel || '';
        fragment.append(oneTimeOption);
      }

      plans.forEach(plan => {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = plan.name;
        fragment.append(option);
      });

      select.replaceChildren(fragment);
      const previousPlanAvailable = plans.some(plan => String(plan.id) === previousValue);
      select.value = previousPlanAvailable ? previousValue : (state.requiresSellingPlan ? String(plans[0]?.id || '') : '');
      select.disabled = plans.length === 0;
      container.hidden = plans.length === 0;
    }

    syncSellingPlanUrl(sellingPlanId) {
      if (this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      if (sellingPlanId) url.searchParams.set('selling_plan', sellingPlanId);
      else url.searchParams.delete('selling_plan');
      window.history.replaceState({}, '', url);
    }

    animateVariantUpdate(elements) {
      if (this.reducedMotion) return;
      [...new Set(elements)].forEach(element => {
        element.getAnimations().forEach(animation => animation.cancel());
        element.animate([
          { opacity: .32, transform: 'translateY(2px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], {
          duration: 240,
          easing: 'cubic-bezier(.22, 1, .36, 1)'
        });
      });
    }

    updateInventory(state) {
      this.querySelectorAll('[data-product-inventory]').forEach(inventory => {
        const threshold = Number(inventory.dataset.lowInventoryThreshold || 0);
        const quantity = Number(state.inventoryQuantity || 0);
        let status = 'in_stock';
        let label = inventory.dataset.inStockLabel;
        if (!state.available) {
          status = 'out_of_stock';
          label = inventory.dataset.outOfStockLabel;
        } else if (state.inventoryManagement && quantity > 0 && quantity <= threshold) {
          status = 'low_stock';
          label = inventory.dataset.showInventoryQuantity === 'true'
            ? inventory.dataset.lowStockCountLabel.replace('[quantity]', quantity)
            : inventory.dataset.lowStockLabel;
        }
        inventory.classList.remove('product-inventory--in_stock', 'product-inventory--low_stock', 'product-inventory--out_of_stock');
        inventory.classList.add(`product-inventory--${status}`);
        const labelElement = inventory.querySelector('[data-product-inventory-label]');
        if (labelElement) labelElement.textContent = label;
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stickySubmitObserver?.disconnect();
      if (this.stickyVisibilityFrame) cancelAnimationFrame(this.stickyVisibilityFrame);
    }
  });
}

if (!customElements.get('product-reassurance-slider')) {
  customElements.define('product-reassurance-slider', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.track = this.querySelector('[data-product-reassurance-track]');
      this.slides = [...this.querySelectorAll('[data-product-reassurance-slide]')];
      this.dots = [...this.querySelectorAll('[data-slider-dot]')];
      this.index = 0;
      if (!this.track || this.slides.length < 2) return;
      const { signal } = this.abortController;
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => {
        this.goTo(index);
        this.restart();
      }, { signal }));
      this.addEventListener('pointerenter', () => this.stop(), { signal });
      this.addEventListener('pointerleave', () => this.start(), { signal });
      this.addEventListener('focusin', () => this.stop(), { signal });
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) this.start();
      }, { signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start(), { signal });
      this.goTo(0);
      this.start();
    }

    get reducedMotion() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    goTo(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
      this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === this.index ? 'true' : 'false'));
    }

    start() {
      this.stop();
      if (this.reducedMotion || this.dataset.autoplay !== 'true' || document.hidden) return;
      this.timer = setInterval(() => this.goTo(this.index + 1), Number(this.dataset.interval) || 5000);
    }

    stop() {
      clearInterval(this.timer);
    }

    restart() {
      this.stop();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.abortController?.abort();
    }
  });
}

if (!customElements.get('intelligent-highlight')) {
  customElements.define('intelligent-highlight', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.stream = this.querySelector('[data-intelligent-highlight-stream]');
      this.message = this.stream?.textContent.trim() || '';
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.runId = 0;
      this.isVisible = false;
      this.isAnimating = false;
      this.buildCharacters();

      const { signal } = this.abortController;
      this.motionPreference.addEventListener('change', () => this.configure(), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else if (this.isVisible) this.start();
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        if (event.target === this || event.target.contains(this)) {
          this.stop();
          this.start();
        }
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.updateCharacterOffsets());
      this.resizeObserver.observe(this.stream);
      requestAnimationFrame(() => this.updateCharacterOffsets());

      this.configure();
    }

    buildCharacters() {
      if (!this.stream || !this.message) {
        this.characters = [];
        return;
      }

      const segments = typeof Intl.Segmenter === 'function'
        ? [...new Intl.Segmenter(document.documentElement.lang, { granularity: 'grapheme' }).segment(this.message)].map(item => item.segment)
        : Array.from(this.message);
      const fragment = document.createDocumentFragment();
      let word = null;

      this.characters = segments.map(character => {
        const span = document.createElement('span');
        span.className = 'product-intelligent-highlight__character';
        if (/\s/u.test(character)) {
          span.classList.add('product-intelligent-highlight__character--space');
          word = null;
          fragment.append(span);
        } else {
          if (!word) {
            word = document.createElement('span');
            word.className = 'product-intelligent-highlight__word';
            fragment.append(word);
          }
          word.append(span);
        }
        span.textContent = character;
        return span;
      });

      this.caret = document.createElement('span');
      this.caret.className = 'product-intelligent-highlight__caret';
      this.stream.replaceChildren(fragment);
      this.characters[0]?.before(this.caret);
    }

    updateCharacterOffsets() {
      if (!this.stream || !this.characters?.length) return;
      const streamRect = this.stream.getBoundingClientRect();
      this.characters.forEach(character => {
        const characterRect = character.getBoundingClientRect();
        character.style.setProperty('--intelligent-character-offset', `${characterRect.left - streamRect.left}px`);
      });
    }

    configure() {
      this.stop();
      this.intersectionObserver?.disconnect();
      this.classList.toggle('is-enhanced', !this.motionPreference.matches);

      if (this.motionPreference.matches || !this.characters.length) return;
      this.intersectionObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        this.isVisible = Boolean(entry?.isIntersecting);
        if (this.isVisible && !document.hidden) this.start();
        else this.stop();
      }, { threshold: .35 });
      this.intersectionObserver.observe(this);
    }

    start() {
      if (this.isAnimating || this.motionPreference.matches || !this.characters.length) return;
      this.isAnimating = true;
      const runId = ++this.runId;
      this.play(runId);
    }

    stop() {
      this.runId += 1;
      this.isAnimating = false;
      this.resetVisualState();
    }

    resetVisualState() {
      this.classList.remove('is-running', 'is-fading', 'is-finished', 'is-complete', 'is-persistent');
      this.characters?.forEach(character => character.classList.remove('is-visible'));
      this.characters?.[0]?.before(this.caret);
    }

    async play(runId) {
      await this.wait(Number(this.dataset.startDelay) || 0);
      if (runId !== this.runId) return;

      while (runId === this.runId) {
        this.classList.remove('is-fading', 'is-finished', 'is-complete');
        this.classList.add('is-running');
        await this.wait(60);
        if (runId !== this.runId) return;

        for (const character of this.characters) {
          character.classList.add('is-visible');
          character.after(this.caret);

          await this.wait(this.characterDelay(character));
          if (runId !== this.runId) return;
        }

        this.classList.add('is-complete');
        if (this.dataset.keepVisible === 'true') {
          this.classList.add('is-persistent');
          this.isAnimating = false;
          return;
        }

        await this.wait(Number(this.dataset.holdDuration) || 2000);
        if (runId !== this.runId) return;
        this.classList.add('is-fading');
        await this.wait(360);
        if (runId !== this.runId) return;

        this.classList.remove('is-running', 'is-fading', 'is-complete');
        this.characters.forEach(character => character.classList.remove('is-visible'));

        if (this.dataset.repeat !== 'true') {
          this.classList.add('is-finished');
          this.isAnimating = false;
          return;
        }

        await this.wait(Number(this.dataset.repeatDelay) || 2000);
      }
    }

    characterDelay(character) {
      const baseDelay = Number(this.dataset.characterSpeed) || 15;
      const value = character.textContent;
      if (/[.!?…]/u.test(value)) return baseDelay + 55;
      if (/[,;:]/u.test(value)) return baseDelay + 28;
      if (/\s/u.test(value)) return Math.max(5, baseDelay * .45);
      return baseDelay;
    }

    wait(duration) {
      return new Promise(resolve => window.setTimeout(resolve, duration));
    }

    disconnectedCallback() {
      this.stop();
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.abortController?.abort();
    }
  });
}
